import React, { useState } from "react";
import { Folder, FolderOpen } from "lucide-react";
import { BaseItem } from "../types";
import { folders } from "../data/folderData";
import SongItem from "./folders/SongItem";
import ContentItem from "./folders/ContentItem";
import ProjectItem from "./folders/ProjectItem";
import FolderAnimation from "./FolderAnimation";

const FolderList: React.FC = () => {
  // Initialize with default open folders
  const initialOpenFolders = folders.reduce(
    (acc, folder) => {
      if (folder.defaultOpen) {
        acc[folder.name] = true;
      }
      return acc;
    },
    {} as { [key: string]: boolean },
  );

  const [openFolders, setOpenFolders] = useState<{ [key: string]: boolean }>(
    initialOpenFolders,
  );

  const toggleFolder = (folderName: string) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  const renderItemName = (item: BaseItem, isNested: boolean) => {
    if (item.url) {
      const [role, company] = item.name.split(", ");
      return (
        <span style={item.font ? { fontFamily: item.font } : undefined}>
          {role},{" "}
          <a
            href={item.url}
            className="company-link"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {company}
          </a>
        </span>
      );
    }

    return (
      <span style={item.font ? { fontFamily: item.font } : undefined}>
        {item.name}
      </span>
    );
  };

  const renderItem = (item: BaseItem, isNested: boolean = false) => {
    if (isNested && item.kind === "PROJECT") {
      return (
        <ProjectItem
          key={item.name}
          name={item.name}
          dateAdded={item.dateAdded}
          size={item.size}
          url={item.url}
          contentUrl={item.contentUrl}
          description={item.description}
        />
      );
    }

    if (isNested && item.kind === "LINK") {
      return (
        <div
          key={item.name}
          className="folder-item nested-item"
          onClick={(e) => {
            e.stopPropagation();
            if (item.url) {
              window.open(item.url, "_blank", "noopener,noreferrer");
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <span className="name-column">
            <span style={item.font ? { fontFamily: item.font } : undefined}>
              {item.name}
            </span>
          </span>
          <span>{item.dateAdded}</span>
          <span>{item.size}</span>
          <span>{item.kind}</span>
        </div>
      );
    }

    if (isNested && item.kind === "AUDIO") {
      return (
        <SongItem
          key={item.name}
          name={item.name}
          dateAdded={item.dateAdded}
          audioUrl={item.audioUrl || ""}
        />
      );
    }

    if (isNested && (item.kind === "IMAGE" || item.kind === "VIDEO")) {
      return (
        <ContentItem
          key={item.name}
          name={item.name}
          dateAdded={item.dateAdded}
          contentUrl={item.contentUrl || ""}
          kind={item.kind}
          size={item.size}
        />
      );
    }

    if (isNested && item.kind === "EXPERIENCE") {
      return (
        <div
          key={item.name}
          className="folder-item nested-item"
          onClick={(e) => {
            e.stopPropagation();
            if (item.url) {
              window.open(item.url, "_blank", "noopener,noreferrer");
            }
          }}
          style={{ cursor: item.url ? "pointer" : "default" }}
        >
          <span className="name-column">{renderItemName(item, isNested)}</span>
          <span>{item.dateAdded}</span>
          <span>{item.size}</span>
          <span>{item.kind}</span>
        </div>
      );
    }

    if (isNested && item.kind === "DEGREE") {
      return (
        <div key={item.name} className="folder-item nested-item">
          <span className="name-column">
            <span style={item.font ? { fontFamily: item.font } : undefined}>
              {item.name}
            </span>
          </span>
          <span>{item.dateAdded}</span>
          <span>{item.size}</span>
          <span>{item.kind}</span>
        </div>
      );
    }

    if (item.Component) {
      return (
        <div className={`folder-item ${isNested ? "nested-item" : ""}`}>
          <span>{item.name}</span>
          <span>{item.dateAdded}</span>
          <span>{item.size}</span>
          <span>{item.kind}</span>
          <item.Component />
        </div>
      );
    }

    return (
      <div
        key={item.name || `empty-${Math.random()}`}
        className={`folder-item ${isNested ? "nested-item" : ""}`}
        onClick={() => !isNested && item.name && toggleFolder(item.name)}
        style={{ cursor: !isNested && item.name ? "pointer" : "default" }}
      >
        <span className="name-column">
          {!isNested && (
            <div className="folder-icon">
              {openFolders[item.name] ? (
                <FolderOpen size={20} />
              ) : (
                <Folder size={20} />
              )}
            </div>
          )}
          {item.name ? renderItemName(item, isNested) : <span>&nbsp;</span>}
        </span>
        <span>{item.dateAdded}</span>
        <span>{item.size}</span>
        <span>{item.kind}</span>
      </div>
    );
  };

  // Filter out empty folders on mobile
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const visibleFolders = isMobile
    ? folders.filter((folder) => {
        // Hide ON_REPEAT folder on mobile (audio player is desktop only)
        if (folder.name === "ON_REPEAT") {
          return false;
        }
        return true;
      })
    : folders;

  return (
    <div className="folder-list">
      <div className="folder-list-header">
        <span>NAME</span>
        <span>DATE ADDED</span>
        <span>SIZE</span>
        <span>KIND</span>
      </div>
      {visibleFolders.map((folder, index) => (
        <FolderAnimation key={folder.name} index={index}>
          <React.Fragment>
            {renderItem(folder)}
            {openFolders[folder.name] &&
              folder.items?.map((item) => renderItem(item, true))}
          </React.Fragment>
        </FolderAnimation>
      ))}
    </div>
  );
};

export default FolderList;
